import ReplayIcon from '@mui/icons-material/Replay';
import SearchIcon from '@mui/icons-material/Search';
import { Box, Button, Chip, FormControlLabel, IconButton, Switch, Tooltip, Typography } from '@mui/material';
import { Stack } from '@mui/system';
import { PageSelectionControlsProps } from './PDFPageSelectionControls.props';
import { actionsBoxSx, PDFPageSelectionControlsContainer } from './PDFPageSelectionControls.styles';

const PDFPageSelectionControls = ({
  allowPageSelection,
  isPopped,
  selectedPages,
  onToggleSelectionMode,
  onPageToggle,
  onClearSelection,
  onProcessPages,
  genJobProcessing,
  canRetriggerCitable,
  handleRenderDocument,
  hasJsonData,
  onSearchToggle,
  searchOpen,
}: PageSelectionControlsProps) => (
  <PDFPageSelectionControlsContainer $allowPageSelection={allowPageSelection} $isPopped={isPopped}>
    <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%">
      <Stack direction="row" alignItems="center">
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', width: '100%' }}>
          <FormControlLabel
            control={
              <Switch
                checked={allowPageSelection}
                onChange={e => onToggleSelectionMode(e.target.checked)}
                color="primary"
              />
            }
            label="Enable Page Selection"
          />
        </Box>

        {allowPageSelection && (
          <>
            <Typography variant="body2" color="text.secondary">
              {selectedPages.size} page{selectedPages.size === 1 ? '' : 's'} selected
            </Typography>

            {selectedPages.size > 0 && (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0 }}>
                {Array.from(selectedPages)
                  .sort((a, b) => a - b)
                  .map(pageNum => (
                    <Chip
                      key={pageNum}
                      label={`Page ${pageNum}`}
                      size="small"
                      color="primary"
                      onDelete={() => onPageToggle(pageNum)}
                      disabled={genJobProcessing}
                    />
                  ))}
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 1, ml: 'auto', flexShrink: 0, alignItems: 'center' }}>
              {selectedPages.size > 0 && (
                <>
                  <Button variant="outlined" size="small" onClick={onClearSelection} disabled={genJobProcessing}>
                    Clear Selection
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={onProcessPages}
                    color="primary"
                    disabled={genJobProcessing}>
                    {genJobProcessing ? 'Processing...' : 'Extract Tables from Selected Pages'}
                  </Button>
                </>
              )}
            </Box>
          </>
        )}
      </Stack>
      <Box sx={actionsBoxSx}>
        {hasJsonData && onSearchToggle && (
          <Tooltip title="Search in document (Ctrl+F)">
            <IconButton
              size="small"
              onClick={onSearchToggle}
              color={searchOpen ? 'primary' : 'default'}
              data-testid="pdf-search-toggle">
              <SearchIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {canRetriggerCitable && (
          <Tooltip title="Re-run citable parsing">
            <IconButton size="small" onClick={handleRenderDocument}>
              <ReplayIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Stack>
  </PDFPageSelectionControlsContainer>
);

export default PDFPageSelectionControls;
